import styles from './index.css';

import * as React from 'react';

import {Block, FormField, Section} from '@forms/LayoutComponents';
import {DateRangeSelect, DurationInput, CCMReCaptcha} from '@forms/Composites';
import {AlternativeDateRangeSelect, AlternativeDurationInput} from '@forms/Composites';
import {Form, Button, Icon} from '@forms/Components';
import {UniversityFieldTripsRFQFormProps} from './definitions';
import initConfig from './config';

const UniversityFieldTripsRFQForm = ({submit, withLabel, defaultValues, $disclaimer, captchaIsActive, captchaSitekey}: UniversityFieldTripsRFQFormProps) => {
	const {data: label} = withLabel();
	const hasLabel = label  && Object.keys(label).length;
	const {getConfig, getLabel} = initConfig(label);

	const [force, updateState] = React.useState(true);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const [hasAlternativeDates, setHasAlternativeDates] = React.useState(false);
    const [captchaAccpeted, setCaptchaAccepted] = React.useState<boolean>(!captchaIsActive);

    React.useEffect(() => {
		hasLabel && force && updateState(false)
	});

	const onSubmit = (formData: any) => {
		if (!isSubmitting) {
			submit(formData);
			setIsSubmitting(true);
		}
	};

	const handleTimeFlexibility = (value: string|number) => {
		if (value === getLabel('quoteRequest.timeFlexibility.1M')) {
			setHasAlternativeDates(true);
		} else {
			setHasAlternativeDates(false);
		}
	}

    const handleCaptcha = (value: string) => {
        if (value) {
            setCaptchaAccepted(true);
        }
    }

	return (
		hasLabel
			? (
				<Form
					onSubmit={onSubmit}
					defaultValues={defaultValues}
					className={styles.form}
				>
					<Section title={getLabel('title')}>
						<FormField {...getConfig('quoteRequest.destination')}/>
					</Section>

					<Section title={getLabel('quoteRequest.offerIncludesAccommodation')}>
						<FormField {...getConfig('quoteRequest.offerIncludesAccommodation')}/>
					</Section>

					<Section title={getLabel('quoteRequest.itinerary')}>
						<DateRangeSelect {...{getLabel}}/>
						<Block>
							<Block half>
								<DurationInput {...{getLabel}}/>
							</Block>
							<Block half>
								<FormField
									{...getConfig('quoteRequest.timeFlexibility')}
									onChange={handleTimeFlexibility}
								/>
							</Block>
						</Block>
					</Section>

					{hasAlternativeDates &&
						<Section title={getLabel('quoteRequest.alternative')}>
							<AlternativeDateRangeSelect {...{getLabel}}/>
							<Block>
								<Block half>
									<AlternativeDurationInput {...{getLabel}}/>
								</Block>
								<Block half>
									<FormField {...getConfig('quoteRequest.alternativeTimeFlexibility')}/>
								</Block>
							</Block>
						</Section>
					}

					<Section title={getLabel('quoteRequest.offerIncludesTransportation')}>
						<FormField {...getConfig('quoteRequest.offerIncludesTransportation')}/>
						<FormField {...getConfig('quoteRequest.departureStation')}/>
						<FormField {...getConfig('quoteRequest.departureAirport')}/>
						<FormField {...getConfig('quoteRequest.departureBusStation')}/>
					</Section>

					<Section title={getLabel('quoteRequest.offerPrice.title')}>
						<FormField {...getConfig('quoteRequest.offerPrice')}/>
					</Section>

					<Section title={getLabel('description.title')}>
						<FormField {...getConfig('description')}/>
					</Section>

					<Section title={getLabel('quoteRequest.offerIncludesParticipants')}>
						<Block half>
							<FormField {...getConfig('quoteRequest.offerIncludesParticipants.totalAmountOfParticipants')}/>
						</Block>
						<Block half>
							<FormField {...getConfig('quoteRequest.offerIncludesSupervisors')}/>
						</Block>
						<Block half>
							<FormField {...getConfig('quoteRequest.customerAge')}/>
						</Block>
						<Block half>
							<FormField {...getConfig('hidden.includesHandicap')}/>
						</Block>
						<Block>
							<FormField {...getConfig('quoteRequest.customerHandicap')}/>
						</Block>
					</Section>

					<Section title={getLabel('customer.title')}>
						<Block half title={getLabel('customer')}>
							<FormField {...getConfig('customer.salutation')}/>
							<FormField {...getConfig('customer.additionalType')}/>
							<FormField {...getConfig('customer.givenName')}/>
							<FormField {...getConfig('customer.familyName')}/>
							<FormField {...getConfig('customer.address.streetAddress')}/>
							<Block half>
								<FormField {...getConfig('customer.address.postalCode')}/>
							</Block>
							<Block half>
								<FormField {...getConfig('customer.address.addressLocality')}/>
							</Block>
							<FormField {...getConfig('customer.email')}/>
							<FormField {...getConfig('customer.telephone')}/>
						</Block>
						<Block half title={getLabel('participant')}>
							<FormField {...getConfig('participant.name')}/>
							<FormField {...getConfig('participant.address.streetAddress')}/>
							<Block half>
								<FormField {...getConfig('participant.address.postalCode')}/>
							</Block>
							<Block half>
								<FormField {...getConfig('participant.address.addressLocality')}/>
							</Block>
						</Block>
					</Section>

					<Section title={getLabel('other.title')}>
						<Block half>
							<FormField {...getConfig('referer')}/>
						</Block>
						<Block half>
							<FormField {...getConfig('newsletter')}/>
						</Block>
                        {captchaIsActive &&
                            <Block>
                                <CCMReCaptcha
                                    onChange={handleCaptcha}
                                    siteKey={captchaSitekey ?? ""}
                                />
                            </Block>
                        }
                    </Section>

					<Section isCentered>
						<div className={styles.disclaimer} dangerouslySetInnerHTML={{__html: $disclaimer.outerHTML}} />
					</Section>

					<Section isCentered>
						<Button
							type="submit"
							isText
							isDisabled={isSubmitting}
						>
							<span>{getLabel('submit')}</span>
							{
								isSubmitting
									? <Icon rotates name="spinner"/>
									: <Icon name="angle-double-right"/>

							}
						</Button>
					</Section>
                    {!captchaAccpeted &&
                        <Section isCentered>
                            <span className={styles.error}>
                                {getLabel('captcha.accept')}
                            </span>
                        </Section>
                    }
				</Form>
			)
			: null
	);
}

export default UniversityFieldTripsRFQForm;
